import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RecentFixture, RecentFixturesState, RecentFixtureFilters } from '@/types/fixtures';
import { RecentFixturesService } from '@/services/recentFixturesService';
import { sortFixturesByDate, isValidFixture } from '@/utils/fixtureUtils';

/**
 * Helper to group recent fixtures by date
 */
function groupRecentFixturesByDate(fixtures: RecentFixture[]): {
  [date: string]: RecentFixture[];
} {
  return fixtures.reduce(
    (groups, fixture) => {
      // Use YYYY-MM-dd format matching Angular implementation
      const date = new Date(fixture.reportedTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }

      groups[dateKey].push(fixture);
      return groups;
    },
    {} as { [date: string]: RecentFixture[] }
  );
}

/**
 * Recent Fixtures Store State Interface with Actions
 */
interface RecentFixturesStoreState extends RecentFixturesState {
  // Actions
  fetchFixtures: (refresh?: boolean, isLoadMore?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  refreshFixtures: () => Promise<void>;
  setSelectedFixture: (fixture: RecentFixture | null) => void;
  clearError: () => void;
  clearCache: () => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: RecentFixtureFilters) => Promise<void>;
  toggleOnSubsFilter: () => Promise<void>;
  toggleOwnFixtureFilter: () => Promise<void>;
  setVesselSizeFilter: (vesselSizeId: number) => Promise<void>;
  fetchVesselSizeAccess: () => Promise<void>;
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: RecentFixturesState = {
  fixtures: [],
  groupedFixtures: {},
  loading: false,
  loadingMore: false,
  error: null,
  hasMore: true,
  page: 1,
  refreshing: false,
  selectedFixture: null,
  filters: {
    isOnSubs: false,
    isOwnFixture: false,
    vesselSizeId: 0,
  },
  searchQuery: '',
  allFixtures: [], // Store unfiltered fixtures for client-side search
  disallowedVesselSizeIds: [], // Vessel sizes the user's company may not view (UX gating only)
};

const PAGE_SIZE = 50;

/**
 * Helper function to validate and clean cached fixtures
 */
function validateCachedFixtures(fixtures: unknown[]): RecentFixture[] {
  return fixtures.filter((f): f is RecentFixture => {
    if (!isValidFixture(f)) return false;
    const rf = f as RecentFixture;
    return (
      typeof rf.scrubberInstalled === 'boolean' &&
      (rf.comingFrom === null || typeof rf.comingFrom === 'string')
    );
  });
}

/**
 * Client-side search filter function
 */
function filterRecentFixturesBySearch(fixtures: RecentFixture[], query: string): RecentFixture[] {
  if (!query.trim()) {
    return fixtures;
  }

  const searchLower = query.toLowerCase().trim();

  return fixtures.filter((fixture) => {
    // Search across multiple fields
    return (
      fixture.vesselName?.toLowerCase().includes(searchLower) ||
      fixture.charterer?.toLowerCase().includes(searchLower) ||
      fixture.operator?.toLowerCase().includes(searchLower) ||
      fixture.trade?.toLowerCase().includes(searchLower) ||
      fixture.broker?.toLowerCase().includes(searchLower) ||
      fixture.status?.toLowerCase().includes(searchLower) ||
      fixture.note?.toLowerCase().includes(searchLower) ||
      fixture.source?.toLowerCase().includes(searchLower) ||
      fixture.comingFrom?.toLowerCase().includes(searchLower)
    );
  });
}

/**
 * Recent Fixtures Store
 */
export const useRecentFixturesStore = create<RecentFixturesStoreState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Fetch fixtures with optional refresh
       */
      fetchFixtures: async (refresh = false, isLoadMore = false) => {
        const { fixtures, page, loading, loadingMore, filters, searchQuery } = get();

        // Prevent multiple simultaneous requests
        if ((loading || loadingMore) && !refresh) {
          return;
        }

        if (refresh) {
          set({ loading: true, error: null, page: 1, loadingMore: false });
        } else if (fixtures.length === 0) {
          set({ loading: true, error: null, loadingMore: false });
        } else if (isLoadMore) {
          set({ loadingMore: true, error: null });
        }

        try {
          const targetPage = isLoadMore ? page : 1;
          const response = await RecentFixturesService.getRecentFixtures(
            targetPage,
            PAGE_SIZE,
            filters
          );

          // Validate fetched data
          const validFixtures = validateCachedFixtures(response.data);

          let newFixtures: RecentFixture[];
          if (isLoadMore) {
            const existingIds = new Set(fixtures.map((f) => f.fixtureId));
            const uniqueNewFixtures = validFixtures.filter((f) => !existingIds.has(f.fixtureId));
            newFixtures = [...fixtures, ...uniqueNewFixtures];
          } else {
            newFixtures = validFixtures;
          }

          // Sort all fixtures
          const sortedAllFixtures = sortFixturesByDate(newFixtures) as RecentFixture[];

          // Apply search filter if query exists
          const filteredFixtures = filterRecentFixturesBySearch(sortedAllFixtures, searchQuery);
          const grouped = groupRecentFixturesByDate(filteredFixtures);

          set({
            allFixtures: sortedAllFixtures, // Store all fixtures
            fixtures: filteredFixtures, // Store filtered fixtures
            groupedFixtures: grouped,
            loading: false,
            loadingMore: false,
            hasMore: response.hasMore && response.data.length === PAGE_SIZE,
            page: isLoadMore ? page + 1 : 2,
            error: null,
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch recent fixtures';
          set({
            loading: false,
            loadingMore: false,
            error: errorMessage,
          });
          console.error('Error fetching recent fixtures:', error);
        }
      },

      /**
       * Load more fixtures (pagination)
       */
      loadMore: async () => {
        const { hasMore, loading, loadingMore } = get();

        if (!hasMore || loading || loadingMore) {
          return;
        }

        await get().fetchFixtures(false, true);
      },

      /**
       * Refresh fixtures (force refresh from server)
       */
      refreshFixtures: async () => {
        set({ refreshing: true });

        try {
          await get().fetchFixtures(true);
        } finally {
          set({ refreshing: false });
        }
      },

      /**
       * Set selected fixture for details view
       */
      setSelectedFixture: (fixture: RecentFixture | null) => {
        set({ selectedFixture: fixture });
      },

      /**
       * Set search query and filter fixtures client-side
       */
      setSearchQuery: (query: string) => {
        const { allFixtures } = get();

        // Filter fixtures based on search query
        const filteredFixtures = filterRecentFixturesBySearch(allFixtures, query);
        const grouped = groupRecentFixturesByDate(filteredFixtures);

        set({
          searchQuery: query,
          fixtures: filteredFixtures,
          groupedFixtures: grouped,
        });
      },

      /**
       * Set filters and fetch data
       */
      setFilters: async (filters: RecentFixtureFilters) => {
        set({ filters, page: 1 });
        await get().fetchFixtures(true);
      },

      /**
       * Toggle On Subs filter
       */
      toggleOnSubsFilter: async () => {
        const { filters } = get();
        const newFilters = {
          ...filters,
          isOnSubs: !filters.isOnSubs,
        };
        set({ filters: newFilters, page: 1 });
        await get().fetchFixtures(true);
      },

      /**
       * Toggle Own Fixture filter
       */
      toggleOwnFixtureFilter: async () => {
        const { filters } = get();
        const newFilters = {
          ...filters,
          isOwnFixture: !filters.isOwnFixture,
        };
        set({ filters: newFilters, page: 1 });
        await get().fetchFixtures(true);
      },

      /**
       * Set vessel size filter (0 = All, 2 = VLCC, 3 = Suezmax) and refetch
       */
      setVesselSizeFilter: async (vesselSizeId: number) => {
        const { filters } = get();
        set({ filters: { ...filters, vesselSizeId }, page: 1 });
        await get().fetchFixtures(true);
      },

      /**
       * Fetch the vessel sizes this user's company may not view, so the UI can hide those filter
       * options. If the currently selected vessel size is now disallowed, fall back to "All" and
       * refetch. UX-only — the backend hard-gates the data regardless.
       */
      fetchVesselSizeAccess: async () => {
        try {
          const disallowedVesselSizeIds = await RecentFixturesService.getRestrictedVesselSizes();
          const { filters } = get();
          const selected = filters.vesselSizeId ?? 0;
          const selectionNowDisallowed =
            selected !== 0 && disallowedVesselSizeIds.includes(selected);

          set({
            disallowedVesselSizeIds,
            ...(selectionNowDisallowed
              ? { filters: { ...filters, vesselSizeId: 0 }, page: 1 }
              : {}),
          });

          if (selectionNowDisallowed) {
            await get().fetchFixtures(true);
          }
        } catch (error) {
          // Non-fatal: leave options as-is (backend still enforces access).
          console.error('Error fetching vessel size access:', error);
        }
      },

      /**
       * Clear error state
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Clear cache and reset
       */
      clearCache: () => {
        set({ ...initialState });
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        set({ ...initialState });
      },
    }),
    { name: 'RecentFixturesStore' }
  )
);

/**
 * Selector hooks for better performance
 */
export const useRecentFixturesLoading = () => useRecentFixturesStore((state) => state.loading);
export const useRecentFixturesLoadingMore = () =>
  useRecentFixturesStore((state) => state.loadingMore);
export const useRecentFixturesError = () => useRecentFixturesStore((state) => state.error);
export const useRecentFixturesData = () =>
  useRecentFixturesStore((state) => ({
    fixtures: state.fixtures,
    groupedFixtures: state.groupedFixtures,
    hasMore: state.hasMore,
  }));
export const useSelectedRecentFixture = () =>
  useRecentFixturesStore((state) => state.selectedFixture);
export const useRecentFixturesFilters = () => useRecentFixturesStore((state) => state.filters);
export const useDisallowedVesselSizeIds = () =>
  useRecentFixturesStore((state) => state.disallowedVesselSizeIds);
